
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDirection } from '@/providers/DirectionProvider';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/providers/AuthProvider';
import { useNavigate, useLocation } from 'react-router-dom';
import PhoneVerification from './PhoneVerification';

const PhoneRequiredDialog: React.FC = () => {
  const { t } = useTranslation();
  const { direction } = useDirection();
  const { user, phoneVerified, checkPhoneVerification } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const checkPhone = async () => {
      // Only show for logged in users who don't have a phone verified
      if (user) {
        // Check database first (source of truth) via checkPhoneVerification
        const hasPhone = await checkPhoneVerification();

        if (hasPhone) {
          console.log('Phone verified in database, not showing dialog');
          setOpen(false);
          return;
        }

        // Only open dialog if user doesn't have a phone verified
        setOpen(true);
      } else {
        setOpen(false);
      }
    };

    checkPhone();
  }, [user, checkPhoneVerification]);

  const handleVerified = () => {
    setOpen(false);
    
    // Check if there's a pending request form
    const storedFormData = localStorage.getItem('ofair_request_form_data');
    
    if (storedFormData) {
      console.log('Found pending form data');
      // If already on home page, just close the dialog - the form is already restored
      if (location.pathname === '/') {
        console.log('Already on home page, staying here');
        return;
      }
      // Navigate back to home page with flag to trigger form restore and open
      navigate('/', { 
        state: { 
          submitPendingRequest: true,
          openRequestForm: true 
        },
        replace: true 
      });
      return;
    }
    
    // If we're on the login page, redirect to dashboard
    if (location.pathname === '/login') {
      navigate('/dashboard');
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="sm:max-w-md"
        dir={direction}
      >
        <DialogHeader>
          <DialogTitle>{t('dialogs.phoneRequired.title')}</DialogTitle>
          <DialogDescription>
            {t('dialogs.phoneRequired.description')}
          </DialogDescription>
        </DialogHeader>

        {user && (
          <PhoneVerification
            phone={user.user_metadata?.phone || ''}
            onVerified={handleVerified}
            isPostLogin={true}
            autoSend={false}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PhoneRequiredDialog;
